const AbstractRepository = require('@/lib/Repositories/AbstractRepository');
const DataStoreNames = require('@/lib/DataStores/DataStoreNames');
const Order = require('@/lib/Orders/Order');
const { basicHydrate } = require('@/lib/Hydration/basicHydrator');
const { createFieldGroupCollectionFromOrder } = require('@/lib/CustomFields/fieldGroupCollectionFactory');

/**
 * This repository is different from others because we don't pull down all the orders during a sync,
 * and we don't clear it during a sync.
 * This is populated when you click the "Download Orders" button from the Settings menu.
 * It stores orders that contain tickets for the current day (at the time Download was pressed)
 * or orders that contain open tickets.
 */
class OrderRepository extends AbstractRepository {
    /**
     * @param {DataStore} dataStore
     * @param {Hydrator} hydrator
     * @param {SyncStateManager} syncStateManager
     * @param orderResource
     */
    constructor(
        dataStore,
        hydrator,
        syncStateManager,
        orderResource
    ) {
        super(
            dataStore,
            hydrator,
            syncStateManager
        );

        this.orderResource = orderResource;
    }

    /**
     * @protected
     */
    getEntityName() {
        return 'Order';
    }

    /**
     * @protected
     */
    getEntityClass() {
        return Order;
    }

    /**
     * @protected
     */
    getTableName() {
        return DataStoreNames.ORDERS;
    }

    /**
     * @protected
     * @return {{query: function(params: object, success:function, error:function)}}
     */
    getResource() {
        return this.orderResource;
    }

    sync() {
        // We don't want to use the regular syncing logic here.
        throw new Error('Use OrderDownloader to download orders.');
    }

    loadFromApi() {
        // We don't want to use the regular syncing logic here.
        throw new Error('Use OrderDownloader to download orders.');
    }

    /**
     * @param {string} bookingRef
     *
     * @return {Promise<Order>}
     */
    findByBookingRef(bookingRef) {
        bookingRef = bookingRef.toUpperCase();

        return this.dataStore.findWhere(this.getTableName(), 'bookingRef', bookingRef)
            .then((data) => {
                // findWhere returns an array, but we know that a bookingRef is like a primary key so there will
                // only be 1 or 0 results.
                if (!data || data.length < 1) {
                    // Throwing an error rejects the promise.
                    throw new Error(`An order with bookingRef ${bookingRef} was not found.`);
                }

                let order = basicHydrate(data[0], new (this.getEntityClass())());
                order.fields = createFieldGroupCollectionFromOrder(order);
                return order;
            });
    }
}

module.exports = OrderRepository;
